"use client";
import { useEffect, useRef, useState } from "react";
import SectionTitle from "./SectionTitle";
import Image from "next/image";
import Link from "next/link";
import Button from "./Button";

type MaxWidth =
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    | "full";

type SectionTitleInputProps = {
    title?: string;
    description?: string[];
    align?: "left" | "center" | "right";
    titleMaxWidth?: MaxWidth;
    descriptionMaxWidth?: MaxWidth;
};

// Right column items now support optional heading + text
export type RightColumnItem = {
    heading?: string;
    text: string;
};

export type TheChallengeScrollSectionProps = {
    sectionTitle?: SectionTitleInputProps;
    rightColumnItems?: RightColumnItem[];
    leftColumnImage?: {
        src: string;
        alt: string;
        width?: number;
        height?: number;
    };
    // Optional button props
    buttonText?: string;
    buttonLink?: string;
    buttonSize?: "sm" | "md" | "lg";
    buttonOnClick?: () => void;
    /** If true, shows auto-incremented numbers (1, 2, 3...) in the dots instead of the red dot. */
    showNumbers?: boolean;
};

export default function TheChallengeScrollSection({
    sectionTitle,
    rightColumnItems = [],
    leftColumnImage,
    buttonText,
    buttonLink,
   
    buttonOnClick,
    showNumbers = false,
}: TheChallengeScrollSectionProps) {
    const sectionRef = useRef<HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isSticky, setIsSticky] = useState(false);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (!sectionRef.current || !containerRef.current) return;

            const section = sectionRef.current;
            const container = containerRef.current;

            // Get section position
            const sectionRect = section.getBoundingClientRect();
            const sectionTop = sectionRect.top;
            const sectionHeight = sectionRect.height;
            const windowHeight = window.innerHeight;

            // Check if section is in view
            const isSectionVisible = sectionTop < windowHeight && sectionTop + sectionHeight > 0;

            if (isSectionVisible) {
                // Check if we've reached the bottom of the scrollable content
                const { scrollTop, scrollHeight, clientHeight } = container;
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

                // Check if we're at the top (for reverse scrolling)
                const isAtTop = scrollTop <= 10;

                setHasReachedEnd(isAtBottom || isAtTop);
                setIsSticky(true);
            } else {
                setIsSticky(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Handle scroll within the container
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleContainerScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            const isAtTop = scrollTop <= 10;
            setHasReachedEnd(isAtBottom || isAtTop);
        };

        container.addEventListener("scroll", handleContainerScroll);
        return () => container.removeEventListener("scroll", handleContainerScroll);
    }, []);

    return (
        <>
            <style>{`
        /* Hide scrollbar completely */
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        /* Timeline connector styles - Full touch top and bottom */
        .timeline-item {
          position: relative;
          padding-bottom: 2.5rem;
        }

        .timeline-item:last-child {
          padding-bottom: 0;
        }

        .timeline-item .timeline-dot {
          position: relative;
          z-index: 2;
        }

        .timeline-item .timeline-line {
          position: absolute;
          left: 0.875rem;
          top: 1.75rem;
          width: 2px;
          height: calc(100% - 1.75rem);
          background: rgb(223, 223, 223);
        }

        /* First item - line goes UP from the dot to the very top */
        .timeline-item:first-child .timeline-line {
          top: -2.5rem;
          height: calc(100% + 2.5rem);
        }

        /* Last item - line goes DOWN from the dot to the very bottom */
        .timeline-item:last-child .timeline-line {
          display: block;
          top: 1.75rem;
          height: calc(100% - 0rem);
        }

        @media (min-width: 1280px) {
          .timeline-item {
            padding-bottom: 3.5rem;
          }
          .timeline-item:last-child {
            padding-bottom: 0;
          }
          .timeline-item .timeline-line {
            left: 1.5rem;
            top: 2.5rem;
            height: calc(100% - 2.5rem);
          }
          .timeline-item:first-child .timeline-line {
            top: -3.5rem;
            height: calc(100% + 3.5rem);
          }
          .timeline-item:last-child .timeline-line {
            top: 2.5rem;
            height: calc(100% - 0rem);
          }
        }

        /* Sticky animation */
        .section-sticky {
          position: sticky;
          top: 0;
          transition: all 0.3s ease;
        }

        .section-unsticky {
          position: relative;
        }

        /* Subtle pulse animation for infinite line feeling */
        @keyframes pulseLine {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }

        .timeline-item:first-child .timeline-line,
        .timeline-item:last-child .timeline-line {
          animation: pulseLine 2.5s ease-in-out infinite;
        }

        /* Arrow indicators for infinite scroll feeling */
        .timeline-item:first-child .timeline-line::before {
          content: '';
          position: absolute;
          top: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-bottom: 6px solid rgb(223, 223, 223);
        }

        .timeline-item:last-child .timeline-line::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 6px solid rgb(223, 223, 223);
        }

        /* Ensure the last line touches the bottom completely */
        .timeline-item:last-child {
          padding-bottom: 0;
        }

        /* Remove any extra spacing at the bottom of the container */
        .timeline-container {
          padding-bottom: 0;
        }
      `}</style>

            <section
                ref={sectionRef}
                className={`relative bg-gray-50/50 h-[80vh] overflow-hidden ${isSticky && !hasReachedEnd ? 'section-sticky' : 'section-unsticky'
                    }`}
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="grid grid-cols-1 xl:grid-cols-2 h-full gap-8 xl:gap-12">
                        {/* Left Column - Title + Optional Image + Optional Button */}
                        <div className="xl:col-span-1 z-50 h-full flex items-center">
                            <div className="z-20 w-full">
                                <div className="max-w-full xl:max-w-lg">
                                    <SectionTitle
                                        title={sectionTitle?.title ?? ""}
                                        {...(sectionTitle?.description && { description: sectionTitle.description })}
                                        align={"left"}
                                        titleMaxWidth={"full"}
                                        descriptionMaxWidth={"full"}
                                    />
                                    
                                    {/* Optional Image in Left Column */}
                                    {leftColumnImage && (
                                        <div className="mt-6 xl:mt-8 relative w-full">
                                            <Image
                                                src={leftColumnImage.src}
                                                alt={leftColumnImage.alt}
                                                width={leftColumnImage.width || 500}
                                                height={leftColumnImage.height || 350}
                                                className="rounded-lg w-full h-auto object-cover"
                                                priority
                                            />
                                        </div>
                                    )}

                                    {/* Optional Button below image */}
                                    {buttonText && (
                                        <div className="mt-6 xl:mt-8">
                                            <Link href={buttonLink || "/contact-us"}>
                                                <Button
                                                    variant="primary"
                                                    {...(buttonOnClick && { onClick: buttonOnClick })}
                                                >
                                                    {buttonText}
                                                </Button>
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Scrollable, No Skew, Hidden Scrollbar */}
                        <div className="xl:col-span-1 z-90 relative h-full w-full min-h-0">
                            <div
                                ref={containerRef}
                                className="flex flex-col relative py-4 xl:py-12 h-full overflow-y-auto hide-scrollbar timeline-container"
                            >
                                {rightColumnItems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="timeline-item flex items-start relative gap-4 xl:gap-6 flex-shrink-0"
                                    >
                                        {/* Timeline line connector */}
                                        <div className="timeline-line"></div>

                                        {/* Timeline dot — shows auto number if showNumbers is true, otherwise red dot */}
                                        <div className="timeline-dot flex flex-col items-center flex-shrink-0">
                                            <div className="relative size-7 xl:size-12 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 z-20">
                                                {showNumbers ? (
                                                    <span className="text-xs xl:text-base font-semibold text-red-600 leading-none">
                                                        {i + 1}
                                                    </span>
                                                ) : (
                                                    <div className="size-2 xl:size-3 rounded-full bg-red-600"></div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0 pt-1 xl:pt-2">
                                            {item.heading && (
                                                <h4 className="text-base xl:text-lg font-semibold text-[#272d57] mb-1">
                                                    {item.heading}
                                                </h4>
                                            )}
                                            <p className="text-[#1b1b1b] text-base xl:text-lg leading-relaxed">
                                                {item.text}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}